import { Component, OnInit, AfterContentInit, OnDestroy, Input, Output, EventEmitter,
		NgZone, OnChanges, SimpleChange, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';
import { ChartUtilitiesService } from './chart-utilities.service';
import { DataLayerService } from './data-layer.service';
import { DashboardTemplateService } from './dashboard-template.service';
import { StationDetailsService } from './station-details.service';
import { ConfigService } from './config.service';
import { AppConfigService, tooltipPreferenceOptions } from './app-config.service';

import * as Highcharts from 'highcharts';
import Annotations from 'highcharts/modules/annotations';
import BrokenAxis from 'highcharts/modules/broken-axis';
import HC_exporting from 'highcharts/modules/exporting';
import HC_exportdata from 'highcharts/modules/export-data';
import { DataWebServicesService } from './data-web-services.service';
Annotations(Highcharts);
BrokenAxis(Highcharts);
HC_exporting(Highcharts);
HC_exportdata(Highcharts);

// Because typscript must have definitions for all functions and highlight is a custom fuction:
declare module 'highcharts' {
	// let doc: any;
	// let win: any;
	interface Point {
		highlight: (event: Highcharts.PointerEventObject) => void;
	}
	interface Chart {
		addButton: (options: any) => void;
		contextMenu: (className, items, x, y, width, height, button, event) => void;
	}
}

declare var L;

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.css'
})
export class ChartComponent {
  @ViewChild ('highchartsPlotContainer', { static: false }) highchartsPlotContainer!:HTMLElement;
	Highcharts: typeof Highcharts = Highcharts;

	// set default tooltip to floating
	// alternative is FIXED
	tooltipPreference = tooltipPreferenceOptions.FLOATING;

	// precip will be shown from the top unless specified to be false
	precipFromTop = true;

	// line color
	color = "";

	// chart type such as "line" or "column"
	chartType = "";

	// start/end date of requested data
	periodStart = 0;
	periodEnd = 0;

	// marker enabled
	markerEnabled = true;

	// tick interval in seconds
	tickIntervalSeconds = 0;

	urlDWS = "";

	constructor(
		private chartUtilitiesService: ChartUtilitiesService,
		private configService: ConfigService,
		private appConfigService: AppConfigService,
		private location: Location,
		private route: ActivatedRoute,
		private dataWebService: DataWebServicesService ) 
	{
		console.log("In chart.component constructor");
		this.configService.setIncludeDecoration(false);
	}

	ngOnInit() {
		this.processRequest();

		// this allows the x-axis and tooltip to display the date in the local browser time zone
		Highcharts.setOptions({
			time: {
				// useUTC: false
				timezone: undefined
			},
			lang: {
				contextButtonTitle: 'Chart options'		// tool tip for the chart menu
			}
		});
	}

	// retrieve the url, set variables for this component, and
	// request the time series from DWS using the remaining query parameters
	processRequest() {
		// get url, remove "chart?", and process query parameters
		const path = this.location.path();
		console.log(path);
		const URLparts = path.split('chart?');
		const decodedURI = decodeURIComponent(URLparts[1]);
		console.log(decodedURI);

		const queryParts = decodedURI.split('&');
		console.log(queryParts);

		
		// add parameters intended for the ts call to DWS in an array.
		// assemble at the end
		const urlDWSArray: any[] = [];

		// process each param=value pair
		queryParts.forEach((q: string) => {
			const qParts = q.split('=');
			const param = qParts[0];
			const value = qParts[1];

			// if the param is known to Operator, process it.
			// otherwise, assume it is for DWS

			switch ( param ) {
				case "tooltipPreference":
					this.tooltipPreference = value == tooltipPreferenceOptions.FIXED? 
								tooltipPreferenceOptions.FIXED :
								tooltipPreferenceOptions.FLOATING;
					break;
				case "precipFromTop": this.precipFromTop = value == 'false'? false : true; break;
				case "color": this.color = value; break;
				case "chartType": this.chartType = value; break;
				case "markerEnabled": this.markerEnabled = value == 'false'? false : true; break;
				case "tickIntervalSeconds": this.tickIntervalSeconds = +value; break;
				case "periodStart": 
							this.periodStart = (new Date(value)).valueOf();
							urlDWSArray.push(q);
					break;
				case "periodEnd": 
							this.periodEnd = (new Date(value)).valueOf();
							urlDWSArray.push(q);
					break;
				default:
					// append the param=value to the url for DWS
					urlDWSArray.push(q);

			}
		})	
		
		// all parameters have been processed - now assemble DWS ts call
		this.urlDWS = "ts?" + urlDWSArray.join("&");

		console.log(this.urlDWS);
		this.dataWebService.getService(this.urlDWS).subscribe ({
			next: (response: any) => {
				// for now, this is just 1 time series but may expand to more
				const chartDefinition = this.createSeriesArray([response]);
				this.displayTimeSeries(chartDefinition);
			}
		})
	}

	createSeriesArray (tsfileArray: any) {

		let seriesArray: any[] = [];
		let yAxis: any[] = [];
		const colorList = this.chartUtilitiesService.getColorList();

		for ( let i=0; i<tsfileArray.length; i++ ) {

			const tsObject = tsfileArray[i].ts;

			if ( tsObject != null ) {
				console.log(tsObject);
				const tsdata = tsObject.data;
				const dataType = tsObject.dataType;
				const isPrecip = dataType.toUpperCase().includes('PRECIP') ? true : false;  // precip should be displayed from the top of the graph
				const color = this.color == "" ? colorList[i % colorList.length]: this.color;
				const chartType = this.getChartType(dataType);
				const units = tsObject.units;
				let seriesLabel = '';  // this is the small legend in the upper right corner of the chart
				const valueDigits = tsObject.hasOwnProperty('valueDigits') ? tsObject.valueDigits : null;
				const precision = valueDigits != null ? valueDigits : 1;
				let gapSize = tsObject.hasOwnProperty('properties') && tsObject.properties.hasOwnProperty('pointNoReportInterval') ? 
								tsObject.properties.pointNoReportInterval*1000 : 86400000;  // # milliseconds in a day

				// create the yAxisLabel, which is also the legend
				// use the label, if specified, from the configuration table, otherwise
				// show the data type and then data interval (unless it includes the word irreg)
				let tsname = tsObject.hasOwnProperty('properties') ? tsObject.properties.pointTagName : '';
				tsname = tsname != null && tsname.length > 0 ? '(' + tsname + ')' : tsname;

				seriesLabel = dataType + tsname + 
						(( tsObject.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + tsObject.dataInterval));
						// console.log(currentTSElement, seriesIdentifierString, seriesIdentifier );

				// YAxis Label
				const yAxisLabel = dataType + (( tsObject.dataInterval.toUpperCase().includes('IRREG')) ? ''  : ('.' + tsObject.dataInterval)) + ' (' + units + ')';

						// line/bar vs markers only
						/*
						let lineWidth = 2;
						let markerEnabled = false;
						let markerSymbol = currentTSElement.hasOwnProperty('symbol') ? currentTSElement.symbol : 'triangle';
						if ( chartType.toUpperCase()==='SCATTER' || (currentTSElement.hasOwnProperty('markerOnly') && currentTSElement.markerOnly === true) ) {
							console.log('marker only');
							lineWidth = 0;
							markerEnabled = true;
							gapSize = 0; // disregard gapSize - show everything if marker only
						}
						*/

				const standardYAxis = {
					title: {
						text: yAxisLabel
					},
					reversed: isPrecip && this.precipFromTop,
					labels: {
								/*
								formatter: function () {
									// this allows the y axis labels to be replace with strings rather than their numeric value
									if ( currentTSElement.hasOwnProperty('yAxisReplace')) {
										return self.replaceValue ( this.value, currentTSElement.yAxisReplace );
									} else {  // just return the numerical value
										return this.value.toFixed(precision);
									}
								}
								*/
					},
					lineWidth: 3
				}
						// don't keep repeating the same yaxis label over and over for the same data type
						/*
						if ( k > 0) {
							standardYAxis.title['enabled'] = false;
							standardYAxis['lineWidth'] = 0;
						}
						*/
						
				yAxis.push ({...standardYAxis });

				// data
				const tsDataHighcharts = [];
				this.chartUtilitiesService.convertTSDataToHighchartsFormat ( tsDataHighcharts, tsObject, dataType, this.markerEnabled, null );

				seriesArray.push (
				{
					data: tsDataHighcharts,
					name: seriesLabel,  // displays data type in the legend
					type: chartType, // defaults to line but we could use "area", etc.
					color: color,
					fillOpacity: 0.3,
					lineWidth: 2,
					marker: {
						enabled: this.markerEnabled,
						// symbol: markerSymbol  // symbol doesn't do anything if enabled is false
					},
					tooltip: {  
						// elements can be accessed during chart formation below through
						// series.tooltipOptions.<element>
						valueDecimals: precision,
						  /*
								tooltipReplace: currentTSElement.hasOwnProperty('tooltipReplace') ? 
									currentTSElement.tooltipReplace : null
									*/
					},
					pointRange: .5 * 3600 * 1000,
					precision: precision
					// sets column width to 30 minute data ... otherwise defaults to smallest time (5 minute data because of stage)
				});

			}

		}
		return { seriesArray: seriesArray, yAxis: yAxis};
	}

	// given a data type, return the chart type such as column or line
	getChartType(dataTypeParam: string) {
		let chartType;
		let dataType = dataTypeParam.toLowerCase();

		if ( this.chartType != "" ) {
			// has been specified in the url - resturn that
			chartType = this.chartType;
		} else if ( dataType == "precip") {
			chartType = "column";
		} else {
			chartType = "line";
		}

		return chartType;
	}

	// seriesArary - each element is its own chart
	// how will we allow multiple time series on a single chart?
	displayTimeSeries(chartDefinition) {
		const seriesArray = chartDefinition.seriesArray;
		const exportMenu = this.chartUtilitiesService.getExportMenuDefinition(true);
		const yAxis = chartDefinition.yAxis;
		const today = new Date();
		const tzName = today.toLocaleString('en', {timeZoneName: 'short'}).split(' ').pop();
		const self = this;
		console.log(seriesArray);

		// should all this be in the loop?  Do we want the start/end date for all time series
		// i.e. should all charts be coordinated
		const startDateSpecified = this.periodStart == 0 ? false : true;
		const endDateSpecified = this.periodEnd == 0 ? false : true;

		let minDate = startDateSpecified ? this.periodStart : today.valueOf();
		let maxDate = endDateSpecified ? this.periodEnd : today.valueOf();
		for ( let i = 0; i < seriesArray.length; i++ ) {
			const ts = seriesArray[i].data;
			console.log(seriesArray[i]);
			console.log(ts);
			if ( ts != null && ts !== undefined && ts.length > 0) {
				// compare min date
				const minDataDate = ts[0].x;
				if ( minDate === null || minDataDate < minDate ) {
					// console.log('setting minDate');
					minDate = minDataDate;
				}

				// compare max date
				const maxDataDate = ts[ts.length - 1].x;
				if ( maxDate === null || maxDataDate > maxDate ) {
					// console.log('setting maxDate');
					maxDate = maxDataDate;
				}
			}
		} 
		console.log(maxDate, minDate);
		const numberOfDays =  (maxDate - minDate) / (24 * 60 * 60 * 1000);
		console.log(maxDate);

		// adjust the chart to have space on the right if end date is close to today
		const showNowLine = today.valueOf() - maxDate < 10000 ? true : false;
		const adjustment =  showNowLine ? this.chartUtilitiesService.getTodayAdjustment (numberOfDays) : 0;
		const nowPlotLine = !showNowLine ? {} :
					{
						color: '#000000', // black
						width: 2,
						value: today.getTime(),
						label: {
							text: 'now',
							rotation: 0,
							textAlign: 'left'
						}
					};

		maxDate = ( showNowLine && maxDate <= today.valueOf()) ?  today.valueOf() + adjustment : maxDate;
		console.log(maxDate);
		const xAxisFormat = this.chartUtilitiesService.formulateXAxisFormat(numberOfDays);
		const tickInterval = this.tickIntervalSeconds == 0 ?
				this.chartUtilitiesService.calculateTickInterval (numberOfDays) :
				this.tickIntervalSeconds * 1000;

		for ( let i = 0; i < seriesArray.length; i++ ) {
			const ts = seriesArray[i].data;
			const gapSize = ts.hasOwnProperty('properties') && ts.properties.hasOwnProperty('pointNoReportInterval') ? ts.properties.pointNoReportInterval*1000 : 86400000;  // # milliseconds in a day

			// make a loop for each time series on this chart
			// for ( let j = 0; j < number of time series; j++)

				let chartConfig: any = 
							{
							chart: {
								marginLeft: 120, // Keep all charts left aligned
								zoomType: "x",
								spacingTop: 20,
								spacingBottom: 20,
								height: 200,
								events: {
									load: this.chartUtilitiesService.addChartRemoveButton
								}
							},
							title: {
								text: '',
								align: 'left',
								margin: 0,
								x: 30
							},
							credits: {
								enabled: false
							},
							xAxis: {
								crosshair: true,
								events: {
									setExtremes: this.chartUtilitiesService.syncExtremes,
								},
								plotLines: [ nowPlotLine ],
								labels: {
									align: 'center',
									/*
									formatter: function() {
										var d = new Date(this.value);
													console.log(d);
													console.log(d.toLocaleDateString());
										return d.toLocaleDateString();
									}
									*/
									format: xAxisFormat
								},
								// lineColor: lineColor,
								lineWidth: 3,
								offset: 10,
								softMin: minDate,
								softMax: maxDate,
								tickInterval: tickInterval,
								tickLength: 10,
								type: 'datetime'
							},
							yAxis: yAxis,
							tooltip: {
								borderWidth: 0,
								distance: 60,
								headerFormat: '{point.x:%b %e %l:%M%p} ' + tzName + '<br>',  
								// see http://pubs.opengroup.org/onlinepubs/007908799/xsh/strftime.html
								style: {
									fontSize: '16px'
								},
								positioner: function() {
									const p = this.chart.hoverPoint;
									return ( self.tooltipPreference == "fixed") ?  {x: 130, y: 0} : { x: p.plotX, y: p.plotY-25};
								},
								shadow: false,
								backgroundColor: 'rgba(255,255,255,0.6)',
								/* 	This works but is supposedly a performance killer.
									See https://jsfiddle.net/BlackLabel/hom49t05/ for their recommendation
								*/
								formatter: function() {
									let tt  = '<b>' + Highcharts.dateFormat('%Y-%m-%e %l:%M%p', this.x) + '</b>';
									const actTime = this.x;
									this.series.chart.series.forEach(function(series, i) {
										// console.log(series); 
										if ( series.points.length > 0 ) {
										const diff = series.closestPointRange;
										let actPoint = series.points[series.points.length - 1];
										let color = series.color;
										series.data.forEach ( function(point, j) {
											if (point.x - actTime < diff ) {
												actPoint = point;
											}
										});
										if ( actPoint.y == null )
											actPoint = series.points[series.points.length - 2];

										let value = actPoint.y.toFixed(series.tooltipOptions.valueDecimals);
										if ( series.tooltipOptions.hasOwnProperty('tooltipReplace') && series.tooltipOptions.tooltipReplace != null ) {
											value = self.replaceValue ( value, series.tooltipOptions.tooltipReplace );
										}
										tt += '<br/>' + '<span style="color:' + color + '">' + value + '</span>';
										}
									});
									/*  different solution from elsewhere
									$(this.points[0].series.chart.series).each(function(i, series) {
										const diff = series.closestPointRange;
										let actPoint = null;
										$.each(series.data, function(i, point) {
											if (actTime - point.x < diff ) {
												actPoint = point;
											}
										});
										tt += '<br/>' + actPoint.y;
									});
									*/
									return tt;
								}
								// valueDecimals: dataset.valueDecimals
							},
							legend: {
								layout: 'horizontal',
								// margin: 30,
								align: 'right',
								// x: 60,
								verticalAlign: 'top',
								y: -25,
								floating: false
							},
							// annotations: annotationArray,
							annotationsOptions: { enabledButtons: false },
							exporting: exportMenu,
							/*
							exporting: {
								buttons: {
									contextButton: {
										// theme: {
											zIndex: 2000
										// }
									}
								}
							},
							*/
							plotOptions: {
								series: {  // general options for all series
									turboThreshold: 100000,  // maximum number of data points, default is 1,000
									states: { inactive: { opacity: 1}}, // added this because the annotation line kept going light
									stickyTracking: false,
									gapSize: gapSize,
									gapUnit: 'value'
								}
							},
							series: seriesArray
						};/*, function(chart) {
							// add the alarm icons
							alarmArray.forEach ( alarm => {
								alarm['iconSize'] = 30;
								const x = chart.xAxis[0].toPixels(alarm.chartAlarmDateTime, false) - (alarm.iconSize / 2);
								const y = alarm.useNearestYValue === true ?
									chart.yAxis[0].toPixels(alarm.nearestYValue, false) - (alarm.iconSize / 2) :
									15;
								const align = alarm.useNearestYValue === true ? 'right' : 'left';
								const pngString = alarm.alarmState === 'Active' ?
									'assets/images/alarm-bell-large.png' :
									'assets/images/alarm-bell-large-actual-gray30x30.png';
								alarm['renderedImage'] = chart.renderer.image(
									pngString, x, y, alarm.iconSize, alarm.iconSize)
									.on('mouseover', function() {
										chart.customTooltip = chart.renderer.label(alarm.alarmMessage, x, y - 40 > 0 ? y - 40 : y + 40, true).attr({
											fill: 'rgba(0,0,0,0.5)',
											align: align,
											zIndex: 14
										}).css({
											fill: 'white'
										}).add();
									}).on('mouseout', function() {
										chart.customTooltip.destroy();
									})
									.add();
							});
							if ( alarmArray.length > 0 ) {
								chart['renderedAlarms'] = alarmArray;
							}
						}
							*/

      			// add this chart to the container
      			// let plotDiv = document.createElement('div');
      			// let appendedDiv = this.highchartsPlotContainer.appendChild(plotDiv);
      			let cr = Highcharts.chart('highchartPlotContainerForKit', chartConfig);

				}
	}

	// given a value, find it in the replacement array and return the replacement string
	replaceValue ( value, replacementArray ) {
		const replaceArray = replacementArray.filter (g => g['value'] == value);
		if ( replaceArray.length > 0 ) {
			return replaceArray[0].label;
		}
		return "";
	}

}
